import React, { memo, useMemo } from 'react';
import { Trash2, Package, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OutwardDataTable = memo(({ 
  bundles, 
  onDeleteBundle, 
  documentBarcode, 
  staffId,
  destination,
  transferType 
}) => {
  // OPTIMIZATION: Only show the first 100 bundles in the table to reduce DOM nodes
  const displayBundles = useMemo(() => bundles.slice(0, 100), [bundles]);
  const hiddenCount = bundles.length - displayBundles.length;

  if (bundles.length === 0) {
    return (
      <div className="text-center py-12 text-[#8A8F9E]">
        <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <p className="text-lg font-medium">No bundles scanned yet</p>
        <p className="text-sm mt-2">Scan bundle QR codes to add them to this outward entry</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4 border-b border-[#E5DFD0]">
        <div>
          <h3 className="text-lg font-semibold text-[#1A1F2E]">
            Outward Entry Summary
          </h3>
          <p className="text-sm text-[#5A5F6E]">
            {bundles.length} bundle{bundles.length !== 1 ? 's' : ''} ready for dispatch
          </p>
        </div>
        <div className="flex gap-4 text-sm">
          {staffId && (
            <div className="bg-[#F5F1E8] px-3 py-1 rounded">
              <span className="text-[#5A5F6E]">Staff:</span>{' '}
              <span className="font-medium text-[#1A1F2E]">{staffId}</span>
            </div>
          )}
          {documentBarcode && (
            <div className="bg-[#F5F1E8] px-3 py-1 rounded">
              <span className="text-[#5A5F6E]">Doc:</span>{' '}
              <span className="font-medium text-[#1A1F2E]">{documentBarcode}</span>
            </div>
          )}
          {destination && (
            <div className="bg-[#FEF2F2] px-3 py-1 rounded">
              <span className="text-[#5A5F6E]">To:</span>{' '}
              <span className="font-medium text-[#B8374A]">{destination}</span>
              <span className="text-xs ml-1 text-gray-500">
                ({transferType === 'job_worker' ? 'Job Worker' : 'Internal'})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bundles Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F5F1E8]">
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">#</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Bundle Barcode</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Scanned At</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-[#1A1F2E]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5DFD0]">
            {displayBundles.map((bundle, index) => (
              <tr key={bundle.bundle_barcode} className="hover:bg-[#FAFAF8]">
                <td className="px-4 py-3 text-sm text-[#5A5F6E]">
                  {bundles.length - index}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#B8374A]" />
                    <span className="font-mono font-medium text-[#1A1F2E]">
                      {bundle.bundle_barcode}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[#5A5F6E]">
                  {bundle.timestamp ? new Date(bundle.timestamp).toLocaleTimeString() : '-'}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteBundle(index)}
                    className="text-[#B8374A] hover:text-[#9A2F3D] hover:bg-[#FEF2F2]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {hiddenCount > 0 && (
              <tr className="bg-gray-50">
                <td colSpan={4} className="text-center text-sm text-gray-500 py-3">
                  ... and {hiddenCount} more bundles (showing first 100 for performance)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Stats */}
      <div className="flex justify-between items-center pt-4 border-t border-[#E5DFD0]">
        <div className="text-sm text-[#5A5F6E]">
          Total bundles to dispatch: <span className="font-semibold text-[#B8374A]">{bundles.length}</span>
        </div>
      </div>
    </div>
  );
});

OutwardDataTable.displayName = 'OutwardDataTable';

export default OutwardDataTable;
